// ============================================================================
// i18n.js — infrastruktura shumë-gjuhëshe (react-i18next).
//
// ARKITEKTURA (shih edhe languages.js):
//   - sq + en janë të BUNDLE-uara statikisht: sq është gjuha burim dhe rezerva
//     përfundimtare, en është gjuha pivot nga e cila bien rezervë të tjerat.
//     Kështu shumica dërrmuese e përdoruesve s'presin asnjë rrjet dhe s'ka
//     kurrë "flash" teksti të papërkthyer.
//   - Çdo gjuhë tjetër ngarkohet ME KËRKESË (lazy) përmes import.meta.glob:
//     Vite e ndan secilën në chunk të vetin, pra 20+ gjuhë NUK e rëndojnë
//     bundle-in fillestar. Shtimi i `locales/de.json` e bën gjermanishten të
//     ngarkueshme automatikisht — pa import të ri, pa ndryshim kodi.
//   - Cilat gjuhë janë AKTIVE vendoset nga Admin Center (konfigurim serveri).
//     Konfigurimi ruhet në cache lokale që nisja të jetë e menjëhershme, dhe
//     rifreskohet në sfond.
//
// Zgjedhja e gjuhës (një burim i vetëm i vërtetë, pa plugin zbulimi — që
// shmang garën e vjetër mes `lng` eksplicit dhe zbulimit asinkron):
//   zgjedhja e përdoruesit → "system" (gjuha e telefonit, nëse ka skedar
//   përkthimi për të) → gjuha primare e caktuar nga admini.
//
// DY LISTA, JO NJË (29 gusht): «gjuhët aktive» te Admin Center-i vendosin çfarë
// SHFAQET te zgjedhësi me dorë. Zbulimi nga pajisja nuk i nënshtrohet asaj
// liste — atij i mjafton ekzistenca e skedarit. Prandaj një telefon norvegjez
// merr norvegjisht sapo `locales/no.json` të ekzistojë, pa u prekur asgjë te
// paneli. Shpjegimi i plotë te `languages.js`.
// ============================================================================

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { supabase } from "./supabaseClient";
import sq from "./locales/sq.json";
import en from "./locales/en.json";
import {
  SOURCE_LANG,
  PIVOT_LANG,
  DEFAULT_LANG_CONFIG,
  sanitizeLangConfig,
  resolveLanguage,
  systemLanguage,
  browserLanguages,
  switcherOptions,
  isSupportedLang,
  langDir,
} from "./languages";

const STORE_LANG = "ft_language";       // zgjedhja e përdoruesit ("system" ose kodi)
const STORE_CONFIG = "ft_lang_config";  // cache e konfigurimit të platformës

function readStore(key) {
  try { return localStorage.getItem(key); } catch (e) { return null; }
}
function writeStore(key, val) {
  try { localStorage.setItem(key, val); } catch (e) { /* e padëmshme */ }
}

// --- Konfigurimi i platformës (cache lokale + parazgjedhje e sigurt) --------
let langConfig = (() => {
  try {
    const raw = readStore(STORE_CONFIG);
    if (raw) return sanitizeLangConfig(JSON.parse(raw));
  } catch (e) { /* cache e prishur — injorohet */ }
  return { ...DEFAULT_LANG_CONFIG };
})();

export function getLanguageConfig() {
  return langConfig;
}

// Cila gjuhë doli nga vetë pajisja. E llogaritur në çast (jo e ruajtur): gjuha
// e telefonit mund të ndryshojë pa u rihapur aplikacioni.
export function detectedSystemLanguage() {
  return systemLanguage({
    config: langConfig,
    navigatorLanguages: browserLanguages(),
    hasTranslations,
  });
}

// Çdo gjuhë që ka VËRTET skedar përkthimi, e lejuar nga lista e mbështetjes.
// Lexohet nga `import.meta.glob`, pra një skedar i ri kapet vetë te build-i i
// ardhshëm — asnjë listë me dorë që nesër nuk përputhet me diskun.
export function translatedLanguages() {
  const out = new Set([SOURCE_LANG, PIVOT_LANG]);
  for (const rruga of Object.keys(localeLoaders)) {
    const m = /\.\/locales\/(.+)\.json$/.exec(rruga);
    if (m && isSupportedLang(m[1])) out.add(m[1]);
  }
  return [...out];
}

// Zgjedhësi: [Sistemi] + të preferuarat e adminit + çdo gjuhë tjetër me skedar.
// Të fundit vijnë të shënuara `iPreferuar: false`, që klienti t'i fshehë pas
// një kliku — të gjitha të zgjedhshme, asnjëra e detyrueshme për sytë.
export function getSwitcherOptions(labelIn = "native") {
  return switcherOptions(langConfig, labelIn, detectedSystemLanguage(), translatedLanguages());
}

// --- Ngarkimi me kërkesë i gjuhëve (auto-zbulim i skedarëve) ---------------
// import.meta.glob pa `eager` → funksione që kthejnë Promise; Vite krijon një
// chunk të veçantë për secilin skedar. Skedarët e rinj kapen automatikisht.
const localeLoaders = import.meta.glob("./locales/*.json");

function loaderFor(code) {
  return localeLoaders[`./locales/${code}.json`] || null;
}

// A ekziston vërtet përkthimi për këtë gjuhë (jo vetëm te regjistri)?
export function hasTranslations(code) {
  return code === SOURCE_LANG || code === PIVOT_LANG || !!loaderFor(code);
}

const loading = new Map(); // code → Promise (shmang ngarkimet e dyfishta)

export function loadLanguage(code) {
  if (!code || i18n.hasResourceBundle(code, "translation")) return Promise.resolve(true);
  if (loading.has(code)) return loading.get(code);
  const load = loaderFor(code);
  if (!load) return Promise.resolve(false);
  const p = load()
    .then((mod) => {
      i18n.addResourceBundle(code, "translation", mod.default || mod, true, true);
      return true;
    })
    .catch(() => false)
    .finally(() => loading.delete(code));
  loading.set(code, p);
  return p;
}

// Nisim gjithmonë me një gjuhë të bundle-uar; nëse e zgjedhura ngarkohet me
// kërkesë, kalojmë te ajo sapo të mbërrijë (pa bllokuar nisjen, pa ekran bosh).
function safeInitial(code) {
  return code === SOURCE_LANG || code === PIVOT_LANG ? code : SOURCE_LANG;
}

// --- Nisja -----------------------------------------------------------------
// `hasTranslations` i jepet zgjidhësit te TË TRI thirrjet. Pa të, gjuha e
// telefonit pranohej vetëm nëse ndodhej te lista e gjuhëve aktive — pra një
// telefon norvegjez merrte shqip derisa dikush ta shënonte norvegjishten te
// Admin Center-i. Shih shpjegimin e plotë te `languages.js`.
const initialLang = resolveLanguage({
  stored: readStore(STORE_LANG),
  config: langConfig,
  navigatorLanguages: browserLanguages(),
  hasTranslations,
});

i18n.use(initReactI18next).init({
  resources: {
    sq: { translation: sq },
    en: { translation: en },
  },
  lng: safeInitial(initialLang),
  // Çelësi është anglisht → rezerva përfundimtare është vetë çelësi.
  fallbackLng: (code) => (code === PIVOT_LANG ? [] : [PIVOT_LANG]),
  // Çelësat janë LITERALË: ata të vjetrit përmbajnë "." (p.sh. "toast.failed"),
  // ata të rinjtë janë vetë teksti shqip dhe mund të përmbajnë "." ose ":".
  keySeparator: false,
  nsSeparator: false,
  returnEmptyString: false,
  // Resurset sq+en janë të bundle-uara → init SINKRON, pa "flash" në ngarkim.
  initImmediate: false,
  interpolation: { escapeValue: false }, // React vetë e mbron nga XSS
  // Mbivendosjet nga Admin Center dhe gjuhët e ngarkuara me kërkesë hyjnë te
  // resurset PAS renderimit të parë. Pa këtë, React s'do ta dinte — teksti i ri
  // do të dukej vetëm pas rifreskimit të faqes.
  react: { bindI18nStore: "added" },
});

// Drejtimi i shkrimit (rtl për arabisht/hebraisht/persisht/urdu/pashto).
function applyDocumentLang(code) {
  try {
    if (typeof document === "undefined") return;
    document.documentElement.lang = code;
    document.documentElement.dir = langDir(code);
  } catch (e) { /* pa rëndësi */ }
}

i18n.on("languageChanged", (lng) => {
  if (readStore(STORE_LANG) !== "system") writeStore(STORE_LANG, lng);
  applyDocumentLang(lng);
  loadOverrides(lng);
});
applyDocumentLang(i18n.language);

if (initialLang !== SOURCE_LANG && initialLang !== PIVOT_LANG) {
  loadLanguage(initialLang).then((ok) => {
    if (ok) i18n.changeLanguage(initialLang);
  });
}

// --- Mbivendosjet e bëra nga Admin Center ----------------------------------
// Vendosen SIPËR skedarëve JSON: një fjalë e përkthyer keq rregullohet nga
// paneli, pa deploy. Nëse RPC-ja s'ekziston ende ose s'ka rrjet — përdoret baza.
const overridesLoaded = new Set();

export async function loadOverrides(code, force = false) {
  if (!code) return false;
  if (!force && overridesLoaded.has(code)) return true;
  overridesLoaded.add(code);
  try {
    const { data, error } = await supabase.rpc("rpc_get_translation_overrides", { p_lang: code, p_app: "main" });
    if (error || !data || typeof data !== "object") return false;
    if (Object.keys(data).length) i18n.addResourceBundle(code, "translation", data, true, true);
    return true;
  } catch (e) {
    return false;
  }
}

// Rifreskim në sfond: cilësimet e platformës + mbivendosjet e gjuhës aktive.
export async function refreshLanguagesFromServer() {
  try {
    const { data, error } = await supabase.rpc("rpc_get_language_settings");
    if (!error && data) await applyLanguageConfig(data);
  } catch (e) { /* pa rrjet → mbetet cache-ja */ }
  await loadOverrides(i18n.language, true);
}
refreshLanguagesFromServer();

// --- Ndryshimi i gjuhës nga përdoruesi -------------------------------------
// `code` mund të jetë edhe "system" — atëherë rizgjidhet nga gjuha e telefonit.
export async function setLanguage(code) {
  writeStore(STORE_LANG, code);
  const target = resolveLanguage({
    stored: code,
    config: langConfig,
    navigatorLanguages: browserLanguages(),
    hasTranslations,
  });
  if (target !== SOURCE_LANG) await loadLanguage(target);
  await i18n.changeLanguage(target);
  return target;
}

// Zgjedhja e ruajtur ("system" ose kodi) — për ta shfaqur te switch-i.
export function getStoredChoice() {
  return readStore(STORE_LANG) || (langConfig.allowSystem ? "system" : langConfig.primary);
}

// --- Përditësimi i konfigurimit nga serveri --------------------------------
// Thirret pasi platforma kthen cilësimet; ruan cache-në dhe rilogarit gjuhën
// (p.sh. nëse admini çaktivizoi gjuhën që përdoruesi kishte zgjedhur).
export async function applyLanguageConfig(raw) {
  langConfig = sanitizeLangConfig(raw);
  writeStore(STORE_CONFIG, JSON.stringify(langConfig));
  const target = resolveLanguage({
    stored: readStore(STORE_LANG),
    config: langConfig,
    navigatorLanguages: browserLanguages(),
    hasTranslations,
  });
  if (target !== i18n.language) {
    if (target !== SOURCE_LANG) await loadLanguage(target);
    await i18n.changeLanguage(target);
  }
  return langConfig;
}

export default i18n;
